// Shared "prayer card" print generator. Produces a reverent, picture-backed printable
// in two layouts: a pocket holy-card (two per page, cut along the dashed border) and a
// full page. Used by the prayer catalogue, the Rosary–Scapular card and the Jesus Prayer card.
import { printCardLayoutScript } from "./printCardLayout";
import { scripturePrintNotice } from "@/data/scripture";
import { SUB_TUUM } from "@/data/historicalPrayers";

const esc = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

const absolute = (src) => {
  if (!src) return "";
  if (/^https?:\/\//.test(src)) return src;
  return `${window.location.origin}${src}`;
};

const blockHtml = (block, index) =>
  `<section class="blk"${block.dir === "rtl" ? ' dir="rtl"' : ""} data-testid="print-language-${index}">
     ${block.label ? `<p class="lang">${esc(block.label)}</p>` : ""}
     <p class="body${block.dir === "rtl" ? " rtl" : ""}${block.italic ? " italic" : ""}">${esc(block.text).replace(/\n/g, "<br>")}</p>
   </section>`;

const cardInner = (card) => {
  const img = absolute(card.image?.src);
  return `
    <div class="ornament" aria-hidden="true"></div>
    ${img ? `<figure class="art"><img src="${esc(img)}" alt="${esc(card.image.alt || card.title)}" />${card.image.credit ? `<figcaption>${esc(card.image.credit)}</figcaption>` : ""}</figure>` : ""}
    ${card.eyebrow ? `<p class="eyebrow">${esc(card.eyebrow)}</p>` : ""}
    <h1 class="title" data-testid="print-prayer-title">${esc(card.title)}</h1>
    ${card.subtitle ? `<p class="subtitle">${esc(card.subtitle)}</p>` : ""}
    <div class="blocks">${(card.blocks || []).map(blockHtml).join("")}</div>
    ${card.note ? `<p class="note">${esc(card.note)}</p>` : ""}
    ${card.source?.url ? `<p class="source"><a data-testid="print-prayer-source" href="${esc(card.source.url)}">${esc(card.source.label || "Source")}</a></p>` : ""}
    ${scripturePrintNotice(card) ? `<p class="note" data-testid="print-scripture-copyright">${esc(scripturePrintNotice(card))}</p>` : ""}
    <p class="footer">${esc(card.footer || "Theotokos · Mater Dei — Ad Iesum per Mariam")}</p>
  `;
};

const DEFAULT_BLESSING = SUB_TUUM.latin;

const backInner = (card) => {
  const back = card.back || {};
  const name = back.name || card.subtitle || card.title;
  const blessing = back.blessing || DEFAULT_BLESSING;
  return `
    <div class="ornament" aria-hidden="true"></div>
    <p class="eyebrow">${esc(back.eyebrow || "Under her mantle")}</p>
    <h2 class="back-name" data-testid="print-card-back-name"${back.dir === "rtl" ? ' dir="rtl"' : ""}>${esc(name)}</h2>
    ${back.blocks ? `<div class="blocks back-blocks">${back.blocks.map((b, i) => blockHtml(b, `back-${i}`)).join("")}</div>` : ""}
    <svg class="cross" width="26" height="34" viewBox="0 0 26 34" aria-hidden="true"><rect x="11" y="0" width="4" height="34" fill="#b8860b"/><rect x="3" y="9" width="20" height="4" fill="#b8860b"/></svg>
    <p class="blessing" data-testid="print-card-back-blessing"${back.dir === "rtl" ? ' dir="rtl"' : ""}>${esc(blessing).replace(/\n/g, "<br>")}</p>
    ${back.blocks && card.note ? `<p class="note">${esc(card.note)}</p>` : ""}
    ${back.blocks && card.source ? `<p class="source"><a href="${esc(card.source.url)}" data-testid="print-back-source-link">${esc(card.source.label)}</a></p>` : ""}
    <p class="footer">${esc(card.footer || "Theotokos · Mater Dei — Ad Iesum per Mariam")}</p>
  `;
};

const documentHtml = (card, layout) => {
  const split = layout === "card" && card.duplexSplit;
  const inner = cardInner(split ? { ...card, blocks: card.blocks.slice(0, card.duplexSplit), note: "", source: null } : card);
  const back = backInner(split ? { ...card, back: { ...card.back, blocks: card.blocks.slice(card.duplexSplit) } } : card);
  const bodyClass = `${layout === "card" ? "as-cards" : "as-page"}${card.compact ? " compact" : ""}${card.longForm ? " long-form" : ""}${split ? " duplex-content" : ""}`;
  const cardsMarkup = layout === "card"
    ? `<div class="sheet"><article class="card holy">${inner}</article><article class="card holy">${inner.replace(/data-testid="([^"]+)"/g, 'data-testid="$1-copy-2"')}</article></div>
       <div class="sheet back-sheet"><article class="card holy back">${back}</article><article class="card holy back">${back.replace(/data-testid="([^"]+)"/g, 'data-testid="$1-copy-2"')}</article></div>`
    : `<div class="sheet"><article class="card page">${inner}</article></div>`;
  return `<!doctype html><html lang="${esc(card.lang || "en")}"><head><meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${esc(card.title)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&family=Source+Sans+3:wght@400;600&display=swap" rel="stylesheet">
  <style>
    :root{--midnight:#102a43;--marian:#1f4e79;--gold:#b8860b;--slate:#5b6c86;--ivory:#fbf9f4;}
    *{box-sizing:border-box;}
    html,body{margin:0;padding:0;background:#e9e6df;font-family:'Source Sans 3',system-ui,sans-serif;color:var(--midnight);}
    .as-page{padding:24px;padding-top:90px;}
    .as-cards .sheet{display:flex;flex-wrap:wrap;gap:14px;justify-content:center;padding:16px;}
    .back-sheet{page-break-before:always;break-before:page;align-items:stretch;}
    .card.holy.back{display:flex;flex-direction:column;align-items:center;text-align:center;justify-content:center;min-height:4.4in;}
    .card.holy.back .eyebrow{margin-bottom:14px;}
    .back-name{font-family:'Cormorant Garamond',Georgia,serif;font-weight:600;font-size:22px;line-height:1.1;margin:0 0 16px;color:var(--marian);}
    .cross{margin:0 auto 16px;display:block;}
    .blessing{font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;font-size:14px;line-height:1.55;color:var(--midnight);margin:0 0 auto;max-width:2.7in;}
    .card.holy.back .footer{border-top:none;margin-top:22px;}
    .card{background:var(--ivory);position:relative;overflow:hidden;}
    .card.holy{width:3.35in;border:1.5px dashed #c9a227;border-radius:14px;padding:20px 20px 16px;margin:0;}
    .card.page{max-width:640px;margin:0 auto;border:1px solid rgba(184,134,11,.35);border-radius:20px;padding:44px 48px 34px;box-shadow:0 20px 60px -40px rgba(16,42,67,.5);}
    .ornament{height:3px;background:linear-gradient(90deg,transparent,var(--gold),transparent);margin-bottom:14px;}
    .art{margin:0 0 14px;text-align:center;}
    .art img{width:100%;max-height:2.5in;object-fit:contain;border-radius:8px;}
    .card.page .art img{max-height:320px;}
    .art figcaption{margin-top:6px;font-size:8px;letter-spacing:.06em;color:var(--slate);}
    .card.page .art figcaption{font-size:10px;}
    .eyebrow{margin:0 0 6px;font-size:8.5px;text-transform:uppercase;letter-spacing:.24em;color:var(--gold);font-weight:600;}
    .card.page .eyebrow{font-size:11px;}
    .title{font-family:'Cormorant Garamond',Georgia,serif;font-weight:600;line-height:1.05;margin:0 0 4px;font-size:20px;color:var(--midnight);}
    .card.page .title{font-size:34px;margin-bottom:6px;}
    .subtitle{margin:0 0 12px;font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;color:var(--slate);font-size:12.5px;}
    .card.page .subtitle{font-size:16px;margin-bottom:22px;}
    .blocks{display:flex;flex-direction:column;gap:12px;}
    .lang{margin:0 0 4px;font-size:8px;text-transform:uppercase;letter-spacing:.22em;color:var(--gold);font-weight:600;}
    .card.page .lang{font-size:10px;}
    .body{margin:0;font-family:'Cormorant Garamond',Georgia,serif;font-size:12.5px;line-height:1.5;color:var(--midnight);white-space:pre-wrap;}
    .card.page .body{font-size:16.5px;line-height:1.62;}
    .body.italic{font-style:italic;}
    .body.rtl{direction:rtl;text-align:right;font-size:15px;}
    .note{margin:12px 0 0;font-size:8.5px;line-height:1.4;color:var(--slate);}
    .card.page .note{font-size:11px;margin-top:18px;}
    .source{margin:8px 0 0;font-size:8.5px;overflow-wrap:anywhere;}
    .card.page .source{font-size:11px;}
    a{color:var(--marian);}
    .footer{margin:14px 0 0;font-size:7.5px;text-transform:uppercase;letter-spacing:.2em;color:var(--slate);border-top:1px solid rgba(184,134,11,.3);padding-top:8px;}
    .card.page .footer{font-size:10px;margin-top:26px;padding-top:14px;}
    .print-bar{position:fixed;top:0;left:0;right:0;z-index:9999;display:flex;flex-wrap:wrap;align-items:center;gap:12px;justify-content:center;padding:11px 16px;background:var(--midnight);color:var(--ivory);box-shadow:0 6px 20px rgba(0,0,0,.25);}
    .print-bar .pb-title{font-size:13px;opacity:.85;}
    .print-bar button{font-family:'Source Sans 3',system-ui,sans-serif;cursor:pointer;border-radius:999px;font-size:13px;}
    .print-bar .pb-btn{background:var(--gold);color:#fff;border:none;padding:9px 22px;font-weight:600;}
    .print-bar .pb-btn:hover{background:#caa42a;}
    .print-bar .pb-close{background:transparent;color:var(--ivory);border:1px solid rgba(255,255,255,.45);padding:8px 16px;}
    body{padding-top:90px;}
    .compact .card.holy{height:6.6in;padding:16px;}
    .compact .card.holy .art img{max-height:1.5in;}
    .compact .card.holy .body{font-size:12px;line-height:1.35;}
    .compact .card.holy .blocks{gap:9px;}
    .compact .card.holy .title{font-size:20px;}
    .duplex-content .card.holy{height:9in;padding:16px;}
    .duplex-content .art img{max-height:1.3in;}
    .duplex-content .title,.duplex-content .back-name{font-size:20px;line-height:1.1;}
    .duplex-content .body{font-size:12px;line-height:1.35;}
    .duplex-content .blocks{gap:10px;}
    .duplex-content .card.holy.back{display:block;}
    .duplex-content .back-blocks{text-align:left;margin-bottom:16px;}
    .duplex-content .blessing{margin:0 auto;font-size:12px;line-height:1.35;}
    .duplex-content .cross{display:none;}
    .duplex-content .card.holy.back .eyebrow{margin-bottom:8px;}
    .duplex-content .back-name{margin-bottom:10px;}
    .long-form .card.page{overflow:visible;}
    .long-form .card.page .art img{max-height:180px;}
    .long-form .blocks{display:block;}
    .long-form .blk{margin-bottom:22px;}
    .long-form .lang{break-after:avoid;}
    .card{max-width:100%;overflow-wrap:anywhere;}
    @media screen and (max-width:600px){.card.page{padding:24px 18px;}.as-page{padding-left:12px;padding-right:12px;}.print-bar{position:sticky;}.as-page,.as-cards{padding-top:0;}.as-cards .sheet{padding:12px;}.compact .card.holy{height:auto;min-height:6.6in;}}
    @page{${card.pageSize === "A4" || card.pageSize === "Letter" ? `size:${card.pageSize};` : ""}margin:12mm;}
    @media print{html,body{background:#fff;padding:0!important;}.card.holy{page-break-inside:avoid;}.print-bar{display:none!important;}.long-form .card.page{border:0;border-radius:0;box-shadow:none;padding:0;max-width:none;}.long-form .blk{break-inside:avoid;}.long-form .body{orphans:3;widows:3;}}
  </style></head>
  <body class="${bodyClass}" data-testid="print-card-body">
    <div class="print-bar" data-testid="print-toolbar">
      <span class="pb-title" data-testid="print-preview-guidance">${layout === "card" ? `Two fronts, then two backs · flip on the long edge.${split ? " Prayers continue on the reverse." : ""}` : "Preview — press Print when it looks right"}</span>
      <button type="button" class="pb-btn" data-testid="print-now-button" onclick="window.print()">Print / Save as PDF</button>
      <button type="button" class="pb-close" data-testid="print-close-button" onclick="window.close()">Close</button>
    </div>
    ${cardsMarkup}
    ${printCardLayoutScript}
  </body></html>`;
};

// Opens a print window for the given card. layout: "card" | "page". Returns "" on success
// or an error message if the browser blocked the pop-up.
export function openPrintCard(card, layout = "page") {
  const w = window.open("", "_blank", "width=760,height=900");
  if (!w) return "Your browser blocked the print window. Please allow pop-ups to print this card.";
  w.document.write(documentHtml(card, layout));
  w.document.close();
  return "";
}
